import type { FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { LDFlagSet } from 'launchdarkly-react-client-sdk';

import { logPageView } from 'helpers/mparticle';
import type { AnalyticsAttributes } from 'helpers/mparticle';

export type PageViewAttributesContext = { searchParams: URLSearchParams; flags: LDFlagSet };

type RouteHandle = {
  analyticsEventName?: string;
  analyticsAttributes?: (context: PageViewAttributesContext) => AnalyticsAttributes;
};

export const PageViewTracker: FunctionComponent = () => {
  const location = useLocation();
  const matches = useMatches();
  const flags = useFlags();
  const lastTrackedMatch = [...matches]
    .reverse()
    .find(m => (m.handle as RouteHandle)?.analyticsEventName != null);
  const handle = lastTrackedMatch?.handle as RouteHandle | undefined;
  const analyticsEventName = handle?.analyticsEventName;

  // Read through a ref so query-string or flag changes don't re-fire the page view.
  const attributesRef = useRef<AnalyticsAttributes | undefined>(undefined);
  attributesRef.current = handle?.analyticsAttributes?.({
    searchParams: new URLSearchParams(location.search),
    flags,
  });

  useEffect(() => {
    if (analyticsEventName != null) {
      logPageView(analyticsEventName, attributesRef.current);
    } else {
      logPageView('view_unmapped_page', { pathname: location.pathname });
    }
  }, [location.pathname, analyticsEventName]);

  return null;
};
